import React, { useEffect, useState } from "react";
import "./CognitiveChart.css"; // Assuming you use a CSS file for styling
import styled from "styled-components";
import { useTranslation } from "react-i18next";

const CognitiveChart = ({ data, overview, overall }) => {
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, score: 0, index: null });
  const { t, i18n } = useTranslation();
  const levels = [
    {
      label: "Quantitative Knowledge",
      score: data?.quantitative_knowledge.score,
      level: overview?.quantitative_knowledge
    },
    {
      label: "Comprehensive Knowledge",
      score: data?.comprehension_knowledge.score,
      level: overview?.comprehension_knowledge
    },
    {
      label: "Visual Reasoning",
      score: data?.visual_reasoning.score,
      level: overview?.visual_reasoning
    },
    {
      label: "Fluid Reasoning",
      score: data?.fluid_reasoning.score,
      level: overview?.fluid_reasoning
    },
  ];

  const description ={
    "Quantitative Knowledge":{
      "low":'Have basic ability to reason numerical concepts and relationships and to manipulate numerical symbols. Can solve few problems related to: basic arithmetic, percentages, average, ratio and proportion, number sequences, graphs or tables, value comparison, combination problem.',
      "moderate": 'Have satisfactory ability to reason numerical concepts and relationships and to manipulate numerical symbols. Can solve several problems related to: basic arithmetic, percentages, average, ratio and proportion, number sequences, graphs or tables, value comparison, combination problem.',
      "high": 'Have good ability to reason numerical concepts and relationships and to manipulate numerical symbols. Can solve all the problems related to: basic arithmetic, percentages, average, ratio and proportion, number sequences, graphs or tables, value comparison, combination problem.'
    },
    "Comprehensive Knowledge":{
      "low": 'Have basic acquired knowledge as well as ability to communicate knowledge and to reason using previously learned experiences or procedures. Can complete few tasks related to: analogies, opposites, fill-in-the-blanks with the most appropriate words, general verbal information, language development, lexical knowledge, communication ability, grammatical sensitivity.',
      "moderate": 'Have satisfactory acquired knowledge as well as ability to communicate knowledge and to reason using previously learned experiences or procedures. Can complete several tasks related to: analogies, opposites, fill-in-the-blanks with the most appropriate words, general verbal information, language development, lexical knowledge, communication ability, grammatical sensitivity.',
      "high": 'Have good acquired knowledge as well as ability to communicate knowledge and to reason using previously learned experiences or procedures. Can complete all the tasks related to: analogies, opposites, fill-in-the-blanks with the most appropriate words, general verbal information, language development, lexical knowledge, communication ability, grammatical sensitivity.'
    },
    "Visual Reasoning":{
      "low": 'Have basic ability to perceive, analyse and synthesize visual patterns and to think with visual patterns including the ability to store and recall visual representations. Can complete few tasks related to: series completion with shapes or symbols, find the odd man out in a given set of shapes or symbols, visualization, rotation speed, closure speed, flexibility of closure, visual memory, spatial scanning, serial perceptual integration, length estimation, perceptual illusions, perceptual alternations, imagery.',
      "moderate": 'Have satisfactory ability to perceive, analyse and synthesize visual patterns and to think with visual patterns including the ability to store and recall visual representations. Can complete several tasks related to: series completion with shapes or symbols, find the odd man out in a given set of shapes or symbols, visualization, rotation speed, closure speed, flexibility of closure, visual memory, spatial scanning, serial perceptual integration, length estimation, perceptual illusions, perceptual alternations, imagery.' ,
      "high": 'Have good ability to perceive, analyse and synthesize visual patterns and to think with visual patterns including the ability to store and recall visual representations. Can complete all the tasks related to: series completion with shapes or symbols, find the odd man out in a given set of shapes or symbols, visualization, rotation speed, closure speed, flexibility of closure, visual memory, spatial scanning, serial perceptual integration, length estimation, perceptual illusions, perceptual alternations, imagery.'
    },
    "Fluid Reasoning":{
      "low": 'Have basic ability to reason form concepts and solve problems using unfamiliar information or novel procedures. Can complete few tasks related to: inductive reasoning, general sequential reasoning, Piagetian reasoning, quantitative reasoning, speed of reasoning.',
      "moderate": 'Have satisfactory ability to reason form concepts and solve problems using unfamiliar information or novel procedures. Can complete several tasks related to: inductive reasoning, general sequential reasoning, Piagetian reasoning, quantitative reasoning, speed of reasoning.' ,
      "high": 'Have good ability to reason form concepts and solve problems using unfamiliar information or novel procedures. Can complete all the tasks related to: inductive reasoning, general sequential reasoning, Piagetian reasoning, quantitative reasoning, speed of reasoning.'
    }
  }
  
  const ProgressIndicator = styled.div`
  width: 24px;
  height: 24px;
  background-color:${(props) => props.color ? props.color : 'white'};
  border: 5px solid white;
  border-radius: 50%;
  position: absolute;
  top: 7px;
  transform: translateY(-50%);
  z-index: 99;
  left: ${(props) => props.position}; // Dynamically change position
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.3);
`;

  const Tooltip = styled.div`
  position: absolute;
  background: ${(props) => props.backgroundColor ? props.backgroundColor : "black"};
  color: white;
  padding: 6px 10px;
  border-radius: 5px;
  font-size: 12px;
  white-space: nowrap;
  pointer-events: none;
  transform: translate(-100%, -100%);
  z-index: 100;
  visibility: ${(props) => (props.visible ? "visible" : "hidden")};
  left: 50%;  // Use the x from state without offset
  `;

  const getIndicatorPosition = (score) => {
    let val = Math.round(score);
    if (val >= 100) return "calc(100% - 12px)"; // Prevents indicator from overflowing
    if (val <= 0) return "0%"; // Ensures indicator stays inside
    return `${val}%`;
  };

  const getSegmentWidths = (score) => {
    const percentage = score;  // Calculate the percentage of the score

    if (percentage <= 8.33) {
      return { segment1: 10, segment2: 25, segment3: 65 }; // Low: 10% for low, 90% for remaining
    }
    if (percentage <= 25) {
      return { segment1: 8.33, segment2: 25, segment3: 66.67 }; // Moderate: 25% for moderate, 66.67% for remaining
    }
    if (percentage > 25 && percentage <= 29.99) {
      // Capture scores between 25 and 30 (including decimals like 29.12)
      return { segment1: 5, segment2: 25, segment3: 75 }; // High: 5% for low, 25% for moderate, 75% for high
    }
    if (percentage <= 33.33) {
      return { segment1: 8.33, segment2: 25, segment3: 66.67 }; // Another "moderate" range adjustment if you want to keep the same segment distribution
    }

    // For higher scores (e.g., 29% which falls under high)
    return { segment1: 10, segment2: 20, segment3: 70 };
  };

  const getColor = (level) => {
    switch (level) {
      case "low":
        return "#FD6BAE";
      case "moderate":
        return "#CA4481";
      case "high":
        return "#8F305C";
      default:
        return "#000"; // fallback color if no match is found
    }
  };

  const getUpperCaseLabel = (level) => {
    switch (level) {
      case "low":
        return "Low";
      case "moderate":
      case "medium":
        return "Moderate";
      case "high":
        return "High";
    }
  }

  const handleMouseEnter = (event, score, index) => {
    setTooltip({
      visible: true,
      x: event.clientX,
      y: event.clientY - 100, // Adjust position above cursor
      score,
      index, // Store the index of the hovered chart segment
    });
  };

  const handleMouseMove = (event) => {
    setTooltip((prev) => ({
      ...prev,
      x: event.clientX,
      y: event.clientY - 100,
    }));
  };

  const handleMouseLeave = () => {
    setTooltip({ visible: false, x: 0, y: 0, score: 0, index: null }); // Reset the tooltip state
  };

  return (
    <div className="chart-container">
      <h2 className="chart-title">{t("Overall Cognitive Ability")}</h2>
      <h3 className="chart-subtitle">{t(getUpperCaseLabel(overall?.level))}</h3>
      {levels.map((item, index) => (
        <div key={index} className="chart-row">
          {/* {tooltip.visible && tooltip.index === index && (
            <Tooltip visible={tooltip.visible} x={tooltip.x} y={tooltip.y} backgroundColor={getColor(item.level)}>
              {tooltip.score}
            </Tooltip>
          )} */}
          <div className="chart-label-div">
            <span className="chart-label">{t(item.label)}</span>
            <div className="chart-level-div" style={{ backgroundColor: getColor(item.level) }}>
              <span className="chart-label" style={{ color: 'white' }}>{t(getUpperCaseLabel(item.level))}</span>
            </div>
            <div className="chart-level-div" style={{ backgroundColor: getColor(item.level) }}>
              <span className="chart-label" style={{ color: 'white' }}>{item.score}%</span>
            </div>
          </div>
          <div style={{ height: "18px", width: "100%" }} className="indicator-overlay"
            onMouseEnter={(e) => handleMouseEnter(e, item.score, index)}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            <div className="chart-bar">
              {(() => {
                const { segment1, segment2, segment3 } = getSegmentWidths(item.score);
                return (
                  <>
                    <div className="bar-segment segment-0" style={{ width: `${segment1}%` }}></div>
                    <div className="bar-segment segment-1" style={{ width: `${segment2}%` }}></div>
                    <div className="bar-segment segment-2" style={{ width: `${segment3}%` }}></div>
                  </>
                );
              })()}
            </div>
            <ProgressIndicator position={getIndicatorPosition(item.score)} color={getColor(item.level)} />
          </div>
          <span className="level-description-label">{t(description[item.label][item.level])}</span>

        </div>
      ))}
      <div className="chart-legend">
        <span className="legend level-0"></span> {t('Low')}
        <span className="legend level-1"></span> {t('Moderate')}
        <span className="legend level-2"></span> {t('High')}
      </div>
    </div>
  );
};

export default CognitiveChart;
